import { Button } from "@/components/ui/button";
import { Link, useLocation } from "react-router-dom";
import { FileText, Menu, ChevronRight } from "lucide-react";
import { ssrDocument } from "@/utils/ssrUtils";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useUser, useClerk } from "@clerk/clerk-react";

const Header = () => {
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const { user, isSignedIn } = useUser();
  const { signOut, openSignIn, openSignUp } = useClerk();


  const handleNavClick = (sectionId: string) => {
    // Only run in browser environment
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    
    if (isHomePage) {
      const section = ssrDocument.getElementById(sectionId);
      if (section) {
        section.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // Navigate to home page with hash - the Index page will handle the scrolling
      window.location.href = `/#${sectionId}`;
    }
  };

  // SSR-safe navigation handler
  const handleNavClickSSR = (sectionId: string) => {
    if (typeof window === 'undefined') {
      // During SSR, just return the hash link
      return `/#${sectionId}`;
    }
    handleNavClick(sectionId);
  };

  const getRedirectUrl = () => (typeof window !== 'undefined' ? window.location.href : 'https://coderide.ai');

  const openAuthFallback = (mode: 'sign-in' | 'sign-up', redirectUrl: string) => {
    if (typeof window === 'undefined') return;
    const fallbackUrl = `https://app.coderide.ai/auth/${mode}?redirect_url=${encodeURIComponent(redirectUrl)}`;
    window.open(fallbackUrl, '_blank', 'noopener');
  };

  const handleSignIn = () => {
    const redirectUrl = getRedirectUrl();

    if (!openSignIn) {
      openAuthFallback('sign-in', redirectUrl);
      return;
    }

    try {
      Promise.resolve(openSignIn({ redirectUrl })).catch(() => openAuthFallback('sign-in', redirectUrl));
    } catch {
      openAuthFallback('sign-in', redirectUrl);
    }
  };

  const handleSignUp = () => {
    const redirectUrl = getRedirectUrl();

    if (!openSignUp) {
      openAuthFallback('sign-up', redirectUrl);
      return;
    }

    try {
      Promise.resolve(openSignUp({ redirectUrl })).catch(() => openAuthFallback('sign-up', redirectUrl));
    } catch {
      openAuthFallback('sign-up', redirectUrl);
    }
  };

  return <header className="sticky top-0 bg-[#050505] z-50 pt-[env(safe-area-inset-top)]">
      <div className="container flex items-center justify-between h-16 px-4 md:px-6 gap-4">
        <Link to="/" className="flex items-center gap-2">
          <img 
            src="https://ideybnueizkxwqmjowpy.supabase.co/storage/v1/object/sign/coderide-website/Logo%20White.svg?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJjb2RlcmlkZS13ZWJzaXRlL0xvZ28gV2hpdGUuc3ZnIiwiaWF0IjoxNzQ0OTIxODI5LCJleHAiOjE3NzY0NTc4Mjl9.Zk_7kzu1sXIdqhsO2IpRSy3sAOhBKkB9XKnhMq5t7Co" 
            alt="CodeRide Logo" 
            className="h-4 w-auto" 
            loading="lazy" 
          />
        </Link>
        <nav className="hidden md:flex items-center gap-6">
          <button 
            onClick={() => handleNavClick('features')}
            className="text-sm font-medium text-white hover:text-coderide-light-blue transition-colors"
          >
            Features
          </button>
          <button 
            onClick={() => handleNavClick('how-it-works')}
            className="text-sm font-medium text-white hover:text-coderide-light-blue transition-colors"
          >
            How it works
          </button>
          <button 
            onClick={() => handleNavClick('benefits')}
            className="text-sm font-medium text-white hover:text-coderide-light-blue transition-colors"
          >
            Benefits
          </button>
          <Link 
            to="/pricing" 
            className="text-sm font-medium text-white hover:text-coderide-light-blue transition-colors"
          >
            Pricing
          </Link>
          <Link 
            to="/prd-generator" 
            className="text-sm font-medium text-white hover:text-coderide-light-blue transition-colors"
          >
            PRD Generator
          </Link>
          <Link 
            to="/blog" 
            className="text-sm font-medium text-white hover:text-coderide-light-blue transition-colors"
          >
            Blog
          </Link>
          <button 
            onClick={() => handleNavClick('faq')}
            className="text-sm font-medium text-white hover:text-coderide-light-blue transition-colors"
          >
            FAQ
          </button>
          <a 
            href="https://help.coderide.ai/docs" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-sm font-medium text-white hover:text-coderide-light-blue transition-colors"
          >
            <FileText className="h-4 w-4" />
            Docs
          </a>
        </nav>

        {/* Mobile navigation */}
        <div className="md:hidden">
          <Sheet>
            <SheetTrigger asChild>
              <button
                aria-label="Open menu"
                className="inline-flex items-center justify-center h-10 w-10 rounded-md border border-white/10 text-white hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-[#050505] text-white p-0 border-l border-white/10">
              <div className="flex flex-col h-full">
                {/* Sheet header */}
                <div className="p-4 border-b border-white/10 flex items-center gap-2">
                  <img 
                    src="https://ideybnueizkxwqmjowpy.supabase.co/storage/v1/object/sign/coderide-website/Logo%20White.svg?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJjb2RlcmlkZS13ZWJzaXRlL0xvZ28gV2hpdGUuc3ZnIiwiaWF0IjoxNzQ0OTIxODI5LCJleHAiOjE3NzY0NTc4Mjl9.Zk_7kzu1sXIdqhsO2IpRSy3sAOhBKkB9XKnhMq5t7Co" 
                    alt="CodeRide Logo" 
                    className="h-5 w-auto" 
                    loading="lazy" 
                  />
                </div>

                {/* Nav items */}
                <nav className="py-2">
                  <a href="/#features" className="px-4 py-3 flex items-center justify-between text-base hover:bg-white/5 rounded-md transition-colors">
                    <span>Features</span><ChevronRight className="h-4 w-4 text-white/40" />
                  </a>
                  <a href="/#how-it-works" className="px-4 py-3 flex items-center justify-between text-base hover:bg-white/5 rounded-md transition-colors">
                    <span>How it works</span><ChevronRight className="h-4 w-4 text-white/40" />
                  </a>
                  <a href="/#benefits" className="px-4 py-3 flex items-center justify-between text-base hover:bg-white/5 rounded-md transition-colors">
                    <span>Benefits</span><ChevronRight className="h-4 w-4 text-white/40" />
                  </a>
                <a href="/pricing" className="px-4 py-3 flex items-center justify-between text-base hover:bg-white/5 rounded-md transition-colors">
                  <span>Pricing</span><ChevronRight className="h-4 w-4 text-white/40" />
                </a>
                <Link to="/prd-generator" className="px-4 py-3 flex items-center justify-between text-base hover:bg-white/5 rounded-md transition-colors">
                  <span>PRD Generator</span><ChevronRight className="h-4 w-4 text-white/40" />
                </Link>
                <a href="/blog" className="px-4 py-3 flex items-center justify-between text-base hover:bg-white/5 rounded-md transition-colors">
                  <span>Blog</span><ChevronRight className="h-4 w-4 text-white/40" />
                </a>
                  <a href="/#faq" className="px-4 py-3 flex items-center justify-between text-base hover:bg-white/5 rounded-md transition-colors">
                    <span>FAQ</span><ChevronRight className="h-4 w-4 text-white/40" />
                  </a>
                  <a href="https://help.coderide.ai/docs" target="_blank" rel="noopener noreferrer" className="px-4 py-3 flex items-center justify-between text-base hover:bg-white/5 rounded-md transition-colors">
                    <span>Docs</span><ChevronRight className="h-4 w-4 text-white/40" />
                  </a>
                </nav>

                {/* Footer CTAs */}
                <div className="p-4 border-t border-white/10">
                  {isSignedIn ? (
                    <div className="flex flex-col gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full px-4 text-sm text-white/80 hover:text-white"
                        onClick={() => signOut()}
                      >
                        Sign Out
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        className="w-full px-4 text-sm"
                        onClick={() => window.open('https://app.coderide.ai/', '_blank', 'noopener')}
                      >
                        To App
                      </Button>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full px-4 text-sm text-white/80 hover:text-white"
                        onClick={handleSignIn}
                      >
                        Log In
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        className="w-full px-4 text-sm"
                        onClick={handleSignUp}
                      >
                        Sign Up
                      </Button>
                    </div>
                  )}
                  {/* Social icons */}
                  <div className="mt-4 flex items-center justify-center gap-5 text-white/70">
                    {/* X (Twitter) */}
                    <a 
                      href="https://x.com/coderide_ai" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      aria-label="X (Twitter)"
                      className="hover:text-coderide-light-blue transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 512 512" fill="currentColor">
                        <path d="M389.2 48h70.6L305.6 224.2 487 464H345L233.7 318.6 106.5 464H35.8L200.7 275.5 13.8 48H156.4l100.5 132.9L389.2 48zm-24.8 373.8h39.1L151.1 88h-42L364.4 421.8z"/>
                      </svg>
                    </a>
                    {/* LinkedIn */}
                    <a 
                      href="https://www.linkedin.com/showcase/coderide/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      aria-label="LinkedIn"
                      className="hover:text-coderide-light-blue transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 448 512" fill="currentColor">
                        <path d="M100.28 448H7.4V148.9h92.88zM53.79 108.1C24.09 108.1 0 83.5 0 53.8a53.79 53.79 0 0 1 107.58 0c0 29.7-24.1 54.3-53.79 54.3zM447.9 448h-92.68V302.4c0-34.7-.7-79.2-48.29-79.2-48.29 0-55.69 37.7-55.69 76.7V448h-92.78V148.9h89.08v40.8h1.3c12.4-23.5 42.69-48.3 87.88-48.3 94 0 111.28 61.9 111.28 142.3V448z"/>
                      </svg>
                    </a>
                    {/* GitHub */}
                    <a 
                      href="https://github.com/PixdataOrg/coderide-mcp/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      aria-label="GitHub"
                      className="hover:text-coderide-light-blue transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 496 512" fill="currentColor">
                        <path d="M165.9 397.4c0 2-2.3 3.6-5.2 3.6-3.3.3-5.6-1.3-5.6-3.6 0-2 2.3-3.6 5.2-3.6 3-.3 5.6 1.3 5.6 3.6zm-31.1-4.5c-.7 2 1.3 4.3 4.3 4.9 2.6 1 5.6 0 6.2-2s-1.3-4.3-4.3-5.2c-2.6-.7-5.5.3-6.2 2.3zm44.2-1.7c-2.9.7-4.9 2.6-4.6 4.9.3 2 2.9 3.3 5.9 2.6 2.9-.7 4.9-2.6 4.6-4.6-.3-1.9-3-3.2-5.9-2.9zM244.8 8C106.1 8 0 113.3 0 252c0 110.9 69.8 205.8 169.5 239.2 12.8 2.3 17.3-5.6 17.3-12.1 0-6.2-.3-40.4-.3-61.4 0 0-70 15-84.7-29.8 0 0-11.4-29.1-27.8-36.6 0 0-22.9-15.7 1.6-15.4 0 0 24.9 2 38.6 25.8 21.9 38.6 58.6 27.5 72.9 20.9 2.3-16 8.8-27.1 16-33.7-55.9-6.2-112.3-14.3-112.3-110.5 0-27.5 7.6-41.3 23.6-58.9-2.6-6.5-11.1-33.3 2.6-67.9 20.9-6.5 69 27 69 27 20-5.6 41.5-8.5 62.8-8.5s42.8 2.9 62.8 8.5c0 0 48.1-33.6 69-27 13.7 34.7 5.2 61.4 2.6 67.9 16 17.7 25.8 31.5 25.8 58.9 0 96.5-58.9 104.2-114.8 110.5 9.2 7.9 17 22.9 17 46.4 0 33.7-.3 75.4-.3 83.6 0 6.5 4.6 14.4 17.3 12.1C428.2 457.8 496 362.9 496 252 496 113.3 383.5 8 244.8 8zM97.2 352.9c-1.3 1-1 3.3.7 5.2 1.6 1.6 3.9 2.3 5.2 1 1.3-1 1-3.3-.7-5.2-1.6-1.6-3.9-2.3-5.2-1zm-10.8-8.1c-.7 1.3.3 2.9 2.3 3.9 1.6 1 3.6.7 4.3-.7.7-1.3-.3-2.9-2.3-3.9-2-.6-3.6-.3-4.3.7zm32.4 35.6c-1.6 1.3-1 4.3 1.3 6.2 2.3 2.3 5.2 2.6 6.5 1 1.3-1.3.7-4.3-1.3-6.2-2.2-2.3-5.2-2.6-6.5-1zm-11.4-14.7c-1.6 1-1.6 3.6 0 5.9 1.6 2.3 4.3 3.3 5.6 2.3 1.6-1.3 1.6-3.9 0-6.2-1.4-2.3-4-3.3-5.6-2z"/>
                      </svg>
                    </a>
                    {/* Email */}
                    <a 
                      href="mailto:hello@coderide.ai" 
                      aria-label="Email"
                      className="hover:text-coderide-light-blue transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 512 512" fill="currentColor">
                        <path d="M64 112c-8.8 0-16 7.2-16 16v22.1L220.5 291.7c20.7 17 50.4 17 71.1 0L464 150.1V128c0-8.8-7.2-16-16-16H64zM48 212.2V384c0 8.8 7.2 16 16 16H448c8.8 0 16-7.2 16-16V212.2L322.5 328.1c-38.4 31.5-93.7 31.5-132 0L48 212.2zM0 128C0 92.7 28.7 64 64 64H448c35.3 0 64 28.7 64 64V384c0 35.3-28.7 64-64 64H64c-35.3 0-64-28.7-64-64V128z"/>
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
        <div className="hidden md:flex items-center gap-4">
          {/* Social Media Icons */}
          <div className="hidden md:flex items-center gap-3">
            <a 
              href="https://github.com/PixdataOrg/coderide-mcp/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-white hover:text-coderide-light-blue transition-colors"
              aria-label="GitHub"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 496 512" className="h-6 w-6" fill="currentColor">
                <path d="M165.9 397.4c0 2-2.3 3.6-5.2 3.6-3.3.3-5.6-1.3-5.6-3.6 0-2 2.3-3.6 5.2-3.6 3-.3 5.6 1.3 5.6 3.6zm-31.1-4.5c-.7 2 1.3 4.3 4.3 4.9 2.6 1 5.6 0 6.2-2s-1.3-4.3-4.3-5.2c-2.6-.7-5.5.3-6.2 2.3zm44.2-1.7c-2.9.7-4.9 2.6-4.6 4.9.3 2 2.9 3.3 5.9 2.6 2.9-.7 4.9-2.6 4.6-4.6-.3-1.9-3-3.2-5.9-2.9zM244.8 8C106.1 8 0 113.3 0 252c0 110.9 69.8 205.8 169.5 239.2 12.8 2.3 17.3-5.6 17.3-12.1 0-6.2-.3-40.4-.3-61.4 0 0-70 15-84.7-29.8 0 0-11.4-29.1-27.8-36.6 0 0-22.9-15.7 1.6-15.4 0 0 24.9 2 38.6 25.8 21.9 38.6 58.6 27.5 72.9 20.9 2.3-16 8.8-27.1 16-33.7-55.9-6.2-112.3-14.3-112.3-110.5 0-27.5 7.6-41.3 23.6-58.9-2.6-6.5-11.1-33.3 2.6-67.9 20.9-6.5 69 27 69 27 20-5.6 41.5-8.5 62.8-8.5s42.8 2.9 62.8 8.5c0 0 48.1-33.6 69-27 13.7 34.7 5.2 61.4 2.6 67.9 16 17.7 25.8 31.5 25.8 58.9 0 96.5-58.9 104.2-114.8 110.5 9.2 7.9 17 22.9 17 46.4 0 33.7-.3 75.4-.3 83.6 0 6.5 4.6 14.4 17.3 12.1C428.2 457.8 496 362.9 496 252 496 113.3 383.5 8 244.8 8zM97.2 352.9c-1.3 1-1 3.3.7 5.2 1.6 1.6 3.9 2.3 5.2 1 1.3-1 1-3.3-.7-5.2-1.6-1.6-3.9-2.3-5.2-1zm-10.8-8.1c-.7 1.3.3 2.9 2.3 3.9 1.6 1 3.6.7 4.3-.7.7-1.3-.3-2.9-2.3-3.9-2-.6-3.6-.3-4.3.7zm32.4 35.6c-1.6 1.3-1 4.3 1.3 6.2 2.3 2.3 5.2 2.6 6.5 1 1.3-1.3.7-4.3-1.3-6.2-2.2-2.3-5.2-2.6-6.5-1zm-11.4-14.7c-1.6 1-1.6 3.6 0 5.9 1.6 2.3 4.3 3.3 5.6 2.3 1.6-1.3 1.6-3.9 0-6.2-1.4-2.3-4-3.3-5.6-2z"/>
              </svg>
            </a>
            <a 
              href="https://x.com/coderide_ai" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-white hover:text-coderide-light-blue transition-colors"
              aria-label="X (Twitter)"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 512 512" className="h-6 w-6" fill="currentColor">
                <path d="M389.2 48h70.6L305.6 224.2 487 464H345L233.7 318.6 106.5 464H35.8L200.7 275.5 13.8 48H156.4l100.5 132.9L389.2 48zm-24.8 373.8h39.1L151.1 88h-42L364.4 421.8z"/>
              </svg>
            </a>
          </div>
          <div className="flex items-center gap-2">
            {isSignedIn ? (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  className="px-4 text-sm text-white/80 hover:text-white"
                  onClick={() => signOut()}
                >
                  Sign Out
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  className="px-4 text-sm"
                  onClick={() => window.open('https://app.coderide.ai/', '_blank', 'noopener')}
                >
                  To App
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  className="px-4 text-sm text-white/80 hover:text-white"
                  onClick={handleSignIn}
                >
                  Log In
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  className="px-4 text-sm"
                  onClick={handleSignUp}
                >
                  Sign Up
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </header>;
};

export default Header;
